import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponseModel } from '../../@types/base';
import { CONSTS } from './const';
import { dispatch } from '../store';
import axios from '../../utils/axios';
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';
import {
  AddLocationModel,
  LocationDetailModel,
  LocationListModel,
  LocationResultModel,
  EditLocationForAPIModel
} from '../../@types/location';

// ----------------------------------------------------------------------

type LocationState = {
  locationError: boolean;
  isLocationLoading: boolean;
  locationList: LocationListModel[];

  locationDetailError: boolean;
  isLocationDetailLoading: boolean;
  locationDetail: LocationDetailModel;

  createLocationModalIsOpen: boolean;
  updateLocationModalIsOpen: boolean;
};

const locationDetailInit: LocationDetailModel = {
  id: 0,
  name: '',
  description: '',
  type: '',
  createdAt: '',
  updatedAt: '',
  companyId: 0,
  cameras: []
};

const initialState: LocationState = {
  locationError: false,
  isLocationLoading: false,
  locationList: [],

  locationDetailError: false,
  isLocationDetailLoading: false,
  locationDetail: locationDetailInit,

  createLocationModalIsOpen: false,
  updateLocationModalIsOpen: false
};

const slice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    // ----------------------------------------------------------------------
    // LOCATION
    // ----------------------------------------------------------------------

    // START LOCATION LOADING
    startLocationLoading(state) {
      state.isLocationLoading = true;
    },

    // HAS ERROR LOCATION
    hasErrorLocation(state, action) {
      state.isLocationLoading = false;
      state.locationError = action.payload;
    },

    // GET ALL LOCATIONS
    getAllLocationsSuccess(state, action) {
      state.isLocationLoading = false;
      state.locationList = action.payload;
    },

    // ----------------------------------------------------------------------
    // LOCATION DETAIL
    // ----------------------------------------------------------------------

    // START LOCATION DETAIL LOADING
    startLocationDetailLoading(state) {
      state.isLocationDetailLoading = true;
    },

    // HAS ERROR LOCATION DETAIL
    hasErrorLocationDetail(state, action) {
      state.isLocationDetailLoading = false;
      state.locationDetailError = action.payload;
    },

    // GET LOCATION DETAIL
    getLocationDetailSuccess(state, action) {
      state.isLocationDetailLoading = false;
      state.locationDetail = action.payload;
    },

    // LOCATION ADD MODAL OPEN
    onToggleCreateLocationOpenModal(state, action) {
      state.createLocationModalIsOpen = action.payload;
    },

    // LOCATION DETAIL MODAL OPEN
    onToggleUpdateLocationOpenModal(state, action) {
      state.updateLocationModalIsOpen = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  onToggleCreateLocationOpenModal,
  onToggleUpdateLocationOpenModal
} = slice.actions;

// ----------------------------------------------------------------------
// LOCATION

export async function getLocationByIdWithReturn(locationId: string) {
  dispatch(slice.actions.startLocationDetailLoading());
  try {
    const response: AxiosResponseModel<LocationResultModel> = await axios
      .get(`${CONSTS.LOCATION_GET_GET_LOCATION_BY_ID}/${locationId}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.getLocationDetailSuccess(response.data?.data));
    } else {
      dispatch(slice.actions.getLocationDetailSuccess(locationDetailInit));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorLocationDetail(error));
    return error.response.data;
  }
}

export function setLocationDetailEmpty() {
  return () => {
    try {
      dispatch(slice.actions.getLocationDetailSuccess(locationDetailInit));
    } catch (error: any) {
      dispatch(slice.actions.hasErrorLocationDetail(error));
    }
  };
}

export async function getAllLocationsWithReturn() {
  dispatch(slice.actions.startLocationLoading());
  try {
    const response: AxiosResponseModel<LocationResultModel> = await axios
      .get(CONSTS.LOCATION_GET_GET_ALL_LOCATIONS)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.getAllLocationsSuccess(response.data?.data));
    } else {
      dispatch(slice.actions.getAllLocationsSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorLocation(error));
    return error.response.data;
  }
}

export async function addLocationWithReturn(location: AddLocationModel) {
  dispatch(slice.actions.startLocationLoading());
  try {
    const response: AxiosResponseModel<LocationResultModel> = await axios
      .post(CONSTS.LOCATION_POST_ADD_LOCATION, location)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      await getAllLocationsWithReturn();
      dispatch(slice.actions.hasErrorLocation(false));
    } else {
      dispatch(slice.actions.hasErrorLocation(response.data?.message || ''));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorLocation(error));
    return error.response.data;
  }
}

export async function editLocationWithReturn(
  locatinoId: string,
  locationInfo: EditLocationForAPIModel
) {
  dispatch(slice.actions.startLocationLoading());
  try {
    const response: AxiosResponseModel<LocationResultModel> = await axios
      .post(`${CONSTS.LOCATION_POST_EDIT_LOCATION}/${locatinoId}`, locationInfo)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      await getAllLocationsWithReturn();
      dispatch(slice.actions.hasErrorLocation(false));
    } else {
      dispatch(slice.actions.hasErrorLocation(response.data?.message || ''));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorLocation(error));
    return error.response.data;
  }
}

export async function deleteLocationWithReturn(locationId: string) {
  // dispatch(slice.actions.startLocationLoading());
  try {
    const response: AxiosResponseModel<LocationResultModel> = await axios
      .delete(`${CONSTS.LOCATION_POST_DELETE_LOCATION}/${locationId}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      await getAllLocationsWithReturn();
      dispatch(slice.actions.hasErrorLocation(false));
    } else {
      dispatch(slice.actions.hasErrorLocation(response.data?.message || ''));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorLocation(error));
    return error.response.data;
  }
}

// ----------------------------------------------------------------------
